import { useCallback, useContext, useEffect, useState } from "react";

import { ArrowBack, Edit } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  getGroupById,
  getGroupStats,
  GroupResource,
} from "@rubi/api-generated";
import { useNavigate, useParams } from "react-router-dom";

import { AuthStateContext } from "@/contexts/AuthContext";

import BypassRulesSection from "./BypassRulesSection";
import {
  GROUP_CHIP_TOOLTIPS,
  GROUP_FIELD_LABELS,
  GroupStats,
  USER_PERMISSION_LABELS,
  USER_PERMISSION_TOOLTIPS,
} from "./types";

const AlertGroupManagementDashboard = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  const { currentUserGroups, checkPermission } = useContext(AuthStateContext);
  const [canViewGroup, setCanViewGroup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showError, setShowError] = useState(false);
  const [group, setGroup] = useState<GroupResource | null>(null);
  const [stats, setStats] = useState<GroupStats>({
    totalMembers: 0,
    activeMembers: 0,
    pendingAlerts: 0,
    bulletin_type_bypasses: [],
    user_bypasses: [],
  });

  const currentGroup = currentUserGroups.find(
    (group) => group.id === Number(groupId),
  );

  useEffect(() => {
    if (!groupId || !currentUserGroups.length) {
      setCanViewGroup(false);
      return;
    }
    const canViewGroup = checkPermission("view", "group", Number(groupId));
    console.log("canViewGroup:", canViewGroup);
    setCanViewGroup(canViewGroup);
  }, [groupId, currentUserGroups]);

  // Fetch group details and stats
  const fetchData = useCallback(async () => {
    try {
      const [groupData, statsData] = await Promise.all([
        getGroupById({ path: { group_id: Number(groupId) } }),
        getGroupStats({ path: { group_id: Number(groupId) } }),
      ]);
      setGroup(groupData.data ?? null);
      setStats({
        totalMembers: statsData.data?.totalMembers ?? 0,
        activeMembers: statsData.data?.activeMembers ?? 0,
        pendingAlerts: statsData.data?.pendingAlerts ?? 0,
        bulletin_type_bypasses:
          statsData.data?.bulletin_type_bypasses?.map((bypass) => ({
            ...bypass,
            created_by: bypass.created_by ?? null,
          })) ?? [],
        user_bypasses: statsData.data?.user_bypasses ?? [],
      });
    } catch (error) {
      console.error("Error fetching group data", error);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    if (groupId) {
      fetchData();
    }
  }, [groupId, fetchData]);

  const navigateToMembers = useCallback(() => {
    navigate(`/rubialert/manage-groups/${groupId}/members`);
  }, [navigate, groupId]);

  const navigateBack = useCallback(() => {
    navigate("/rubialert/manage-groups", { replace: true });
  }, [navigate]);

  const navigateToEdit = useCallback(() => {
    navigate(`/rubialert/manage-groups/${groupId}/edit`);
  }, [navigate, groupId]);

  const canManageBulletinSharing = checkPermission(
    "manage",
    "bulletin_sharing",
    Number(groupId),
  );

  const navigateToPendingAlerts = useCallback(() => {
    navigate(`/rubialert/manage-groups/${groupId}/bulletin-share/pending`);
  }, [navigate, groupId]);

  const handleCloseError = () => {
    setShowError(false);
    setErrorMessage("");
  };

  if (!canViewGroup) {
    return (
      <div>
        <Button
          onClick={() =>
            navigate("/rubialert/manage-groups", { replace: true })
          }
        >
          Back to Groups
        </Button>
        You do not have permission to view this group
      </div>
    );
  }

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <div className="group-dashboard-container">
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBack />}
            onClick={navigateBack}
            style={{ marginBottom: 20 }}
          >
            Back to Groups
          </Button>
          <Typography variant="h4" gutterBottom>
            {group?.name ?? currentGroup?.name} Dashboard
          </Typography>
        </Grid>

        {/* Group Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="h6" sx={{ mb: 0 }}>
                Group Information
              </Typography>
              {checkPermission("manage", "group", Number(groupId)) && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Edit />}
                  onClick={navigateToEdit}
                >
                  Edit Group
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  {GROUP_FIELD_LABELS.name}
                </Typography>
                <Typography variant="body1">
                  {group?.name ?? currentGroup?.name}
                </Typography>
              </Box>
              {group?.description && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    {GROUP_FIELD_LABELS.description}
                  </Typography>
                  <Typography variant="body1">{group.description}</Typography>
                </Box>
              )}
              {group?.rubi_org && (
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    {GROUP_FIELD_LABELS.organization}
                  </Typography>
                  <Typography variant="body1">
                    {group.rubi_org.title}
                    {group.rubi_org.short_title && (
                      <Typography
                        component="span"
                        variant="body2"
                        color="textSecondary"
                        sx={{ ml: 1 }}
                      >
                        ({group.rubi_org.short_title})
                      </Typography>
                    )}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                {(group?.is_public ?? currentGroup?.is_public) && (
                  <Tooltip title={GROUP_CHIP_TOOLTIPS.isPublic}>
                    <Chip
                      label={GROUP_FIELD_LABELS.isPublic}
                      color="primary"
                      size="small"
                    />
                  </Tooltip>
                )}
                {(group?.requires_admittance_moderation ??
                  currentGroup?.requires_admittance_moderation) && (
                  <Tooltip title={GROUP_CHIP_TOOLTIPS.requiresModeration}>
                    <Chip
                      label={GROUP_FIELD_LABELS.requiresModeration}
                      color="warning"
                      size="small"
                    />
                  </Tooltip>
                )}
                {(group?.is_police_service ??
                  currentGroup?.is_police_service) && (
                  <Tooltip title={GROUP_CHIP_TOOLTIPS.policeService}>
                    <Chip
                      label={GROUP_FIELD_LABELS.policeService}
                      color="info"
                      size="small"
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Your Permissions */}
        {currentGroup && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Your Permissions
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    {USER_PERMISSION_LABELS.role}
                  </Typography>
                  <Chip
                    label={currentGroup.role?.toUpperCase() ?? "MEMBER"}
                    color={
                      currentGroup.role === "owner"
                        ? "error"
                        : currentGroup.role === "admin"
                          ? "warning"
                          : "default"
                    }
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                  {currentGroup.bypass_moderation && (
                    <Tooltip title={USER_PERMISSION_TOOLTIPS.bypassModeration}>
                      <Chip
                        label={USER_PERMISSION_LABELS.bypassModeration}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                  {currentGroup.moderate_bulletin_admittance && (
                    <Tooltip
                      title={
                        USER_PERMISSION_TOOLTIPS.moderateBulletinAdmittance
                      }
                    >
                      <Chip
                        label={
                          USER_PERMISSION_LABELS.moderateBulletinAdmittance
                        }
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                  {currentGroup.manage_members && (
                    <Tooltip title={USER_PERMISSION_TOOLTIPS.manageMembers}>
                      <Chip
                        label={USER_PERMISSION_LABELS.manageMembers}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                  {currentGroup.manage_admins && (
                    <Tooltip title={USER_PERMISSION_TOOLTIPS.manageAdmins}>
                      <Chip
                        label={USER_PERMISSION_LABELS.manageAdmins}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                  {/* {currentGroup.flag_bulletin && (
                    <Tooltip title={USER_PERMISSION_TOOLTIPS.flagBulletin}>
                      <Chip
                        label={USER_PERMISSION_LABELS.flagBulletin}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )} */}
                  {currentGroup.manage_group && (
                    <Tooltip title={USER_PERMISSION_TOOLTIPS.manageGroup}>
                      <Chip
                        label={USER_PERMISSION_LABELS.manageGroup}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Bypass Rules - Only show when group requires moderation */}
        {(group?.requires_admittance_moderation ??
          currentGroup?.requires_admittance_moderation) && (
          <Grid size={{ xs: 12 }}>
            <BypassRulesSection
              groupId={Number(groupId)}
              userBypasses={stats.user_bypasses ?? []}
              bulletinTypeBypasses={stats.bulletin_type_bypasses ?? []}
              canEdit={checkPermission("manage", "admins", Number(groupId))}
              onUpdate={fetchData}
            />
          </Grid>
        )}

        {/* Stats Cards */}
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Members
              </Typography>
              <Typography variant="h4">{stats.totalMembers}</Typography>
              <Button
                variant="text"
                color="primary"
                onClick={navigateToMembers}
              >
                View Members
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending Bulletin Shares
              </Typography>
              <Typography variant="h4">{stats.pendingAlerts}</Typography>
              <Tooltip
                title={
                  !canManageBulletinSharing
                    ? "You don't have moderator permission to review pending bulletins. Please reach out to group administrator to grant you access."
                    : ""
                }
              >
                <span>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={navigateToPendingAlerts}
                    disabled={!canManageBulletinSharing}
                  >
                    View Pending Bulletin Shares
                  </Button>
                </span>
              </Tooltip>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={showError}
        autoHideDuration={2000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AlertGroupManagementDashboard;
